import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, makeStyles, TextField, Typography } from '@material-ui/core';
import { Comment } from '@material-ui/icons';
import { deleteClientComment, getClientComments, saveClientComment, updateClientComment } from 'api';
import useError from 'components/ErrorProvider/useError';
import { IClientCommentModel } from 'interfaces';
import CommentCard from './CommentCard';

interface IProps {
  phone?: string | null;
}

const MAX_LENGTH = 2000;

// Карточка открывается с двумя комментариями; «Показать ещё» догружает по 10, «Свернуть» возвращает к двум.
const INITIAL_SIZE = 2;
const CHUNK_SIZE = 10;

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(3),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 12,
    color: theme.palette.primary.main,
  },
  headerIcon: {
    fontSize: 18,
  },
  headerTitle: {
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginLeft: 6,
  },
  headerCount: {
    fontSize: 12,
    color: '#98a2b3',
    marginLeft: 6,
  },
  form: {
    border: '1px solid #dfe4ec',
    borderRadius: 6,
    backgroundColor: '#fff',
    padding: '10px 12px',
    marginBottom: 12,
  },
  formInput: {
    fontSize: 14,
  },
  formBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 8,
    paddingTop: 8,
    borderTop: '1px solid #eef1f5',
  },
  hint: {
    fontSize: 11.5,
    color: '#98a2b3',
  },
  kbd: {
    fontSize: 11,
    backgroundColor: '#f1f3f7',
    border: '1px solid #e0e5ec',
    borderBottomWidth: 2,
    borderRadius: 4,
    padding: '0 4px',
  },
  addButton: {
    textTransform: 'none',
    flexShrink: 0,
  },
  list: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridGap: 12,
    alignContent: 'start',
  },
  moreBar: {
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
    marginTop: 12,
  },
  moreButton: {
    textTransform: 'none',
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  empty: {
    gridColumn: '1/-1',
    textAlign: 'center',
    color: '#98a2b3',
    fontSize: 13,
    padding: '34px 10px',
  },
}));

// операции над списком — на уровне модуля: внутри колбэков setComments вложенность
// функций упиралась бы в лимит (компонент → обработчик → then → setComments → map)
const replaceComment = (
  comments: IClientCommentModel[],
  id: number,
  updated: IClientCommentModel,
): IClientCommentModel[] => comments.map((comment) => (comment.id === id ? updated : comment));

const removeComment = (comments: IClientCommentModel[], id: number): IClientCommentModel[] =>
  comments.filter((comment) => comment.id !== id);

// смещение порции считается по числу показанных карточек, поэтому добавленный за это время
// комментарий сдвинул бы выдачу — уже показанные записи из порции отбрасываем
const appendComments = (comments: IClientCommentModel[], chunk: IClientCommentModel[]): IClientCommentModel[] => {
  const shown = new Set(comments.map((comment) => comment.id));
  return [...comments, ...chunk.filter((comment) => !shown.has(comment.id))];
};

/**
 * Комментарии от управляющей компании в карточке клиента: форма добавления и список
 * в один столбец. При открытии грузятся два комментария, кнопка «Показать ещё» догружает
 * следующие десять, «Свернуть» оставляет первые два. Свой комментарий автор может править и удалять, администратор —
 * удалить любой (права приходят с бэка).
 */
const ClientComments: React.FC<IProps> = ({ phone }) => {
  const classes = useStyles();
  const { addError } = useError();

  const [comments, setComments] = useState<IClientCommentModel[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');

  const load = useCallback(() => {
    if (!phone) {
      setComments([]);
      setTotalCount(0);
      return;
    }

    setLoading(true);
    getClientComments(phone, 0, INITIAL_SIZE)
      .then((data) => {
        setComments(data.list || []);
        setTotalCount(data.totalCount || 0);
      })
      .catch((error) => addError(error.response?.data?.message || 'Не удалось загрузить комментарии'))
      .finally(() => setLoading(false));
  }, [phone]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(load, [load]);

  const handleLoadMore = () => {
    if (!phone || loadingMore) return;

    setLoadingMore(true);
    getClientComments(phone, comments.length, CHUNK_SIZE)
      .then((data) => {
        setComments((prevState) => appendComments(prevState, data.list || []));
        setTotalCount(data.totalCount || 0);
      })
      .catch((error) => addError(error.response?.data?.message || 'Не удалось загрузить комментарии'))
      .finally(() => setLoadingMore(false));
  };

  const handleCollapse = () => {
    setComments((prevState) => prevState.slice(0, INITIAL_SIZE));
    setEditId(null);
  };

  const handleAdd = () => {
    const value = newComment.trim();
    if (!value || !phone || saving) return;

    setSaving(true);
    saveClientComment(phone, value)
      .then((comment) => {
        setComments((prevState) => [comment, ...prevState]);
        setTotalCount((prevState) => prevState + 1);
        setNewComment('');
      })
      .catch((error) => addError(error.response?.data?.message || 'Не удалось добавить комментарий'))
      .finally(() => setSaving(false));
  };

  const handleSaveEdit = (id: number) => {
    const value = editValue.trim();
    if (!value) return;

    setSaving(true);
    updateClientComment(id, value)
      .then((updated) => {
        setComments((prevState) => replaceComment(prevState, id, updated));
        setEditId(null);
        setEditValue('');
      })
      .catch((error) => addError(error.response?.data?.message || 'Не удалось изменить комментарий'))
      .finally(() => setSaving(false));
  };

  const handleDelete = (id: number) => {
    setSaving(true);
    deleteClientComment(id)
      .then(() => {
        setComments((prevState) => removeComment(prevState, id));
        setTotalCount((prevState) => Math.max(0, prevState - 1));
      })
      .catch((error) => addError(error.response?.data?.message || 'Не удалось удалить комментарий'))
      .finally(() => setSaving(false));
  };

  const handleInputKeyDown = (event: React.KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      handleAdd();
    }
  };

  const renderList = (): React.ReactNode => {
    if (loading) {
      return (
        <Box className={classes.loader}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (!comments.length) {
      return (
        <Box className={classes.list}>
          <Box className={classes.empty}>
            Комментариев пока нет.
            <br />
            Первый комментарий поможет следующему оператору.
          </Box>
        </Box>
      );
    }

    return (
      <Box className={classes.list}>
        {comments.map((comment) => (
          <CommentCard
            key={comment.id}
            comment={comment}
            maxLength={MAX_LENGTH}
            saving={saving}
            editing={editId === comment.id}
            editValue={editValue}
            onEditValueChange={setEditValue}
            onEditStart={() => {
              setEditId(comment.id);
              setEditValue(comment.comment);
            }}
            onEditCancel={() => setEditId(null)}
            onEditSave={() => handleSaveEdit(comment.id)}
            onDelete={() => handleDelete(comment.id)}
          />
        ))}
      </Box>
    );
  };

  const remaining = Math.max(0, totalCount - comments.length);
  const canCollapse = comments.length > INITIAL_SIZE;

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Comment className={classes.headerIcon} />
        <Typography className={classes.headerTitle}>Комментарии от управляющей компании</Typography>
        {totalCount > 0 && <Typography className={classes.headerCount}>({totalCount})</Typography>}
      </Box>

      <Box className={classes.form}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
          value={newComment}
          disabled={saving}
          placeholder="Комментарий по клиенту: договорённости, обещания, что важно знать следующему оператору…"
          InputProps={{ disableUnderline: true, className: classes.formInput }}
          inputProps={{ maxLength: MAX_LENGTH }}
          onChange={(event) => setNewComment(event.target.value)}
          onKeyDown={handleInputKeyDown}
        />
        <Box className={classes.formBar}>
          <Typography className={classes.hint}>
            <span className={classes.kbd}>Ctrl</span> + <span className={classes.kbd}>Enter</span> — отправить ·{' '}
            {newComment.length}/{MAX_LENGTH}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="small"
            className={classes.addButton}
            disabled={saving || !newComment.trim()}
            onClick={handleAdd}
          >
            Добавить
          </Button>
        </Box>
      </Box>

      {renderList()}

      {(remaining > 0 || canCollapse) && !loading && (
        <Box className={classes.moreBar}>
          {remaining > 0 && (
            <Button
              variant="outlined"
              size="small"
              className={classes.moreButton}
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={14} /> : undefined}
              onClick={handleLoadMore}
            >
              Показать ещё ({remaining})
            </Button>
          )}
          {canCollapse && (
            <Button size="small" className={classes.moreButton} disabled={loadingMore} onClick={handleCollapse}>
              Свернуть
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ClientComments;
